/**
 * EditFirewallRuleModal Component
 *
 * Modal for editing existing firewall rules.
 */

import { Dialog, Transition, Listbox } from '@headlessui/react'
import {
  XIcon,
  ShieldIcon,
  ChevronDownIcon,
  CheckIcon,
  AlertCircleIcon,
  Loader2Icon,
} from 'lucide-react'
import { useState, useEffect, Fragment } from 'react'

import type {
  FirewallRule,
  FirewallRuleUpdate,
  FirewallDirection,
  FirewallAction,
  FirewallProtocol,
} from '@nebula/shared/types/firewall'
import { getApiErrorText } from '@nebula/shared/utils'

type VMInterface = {
  name: string
  network: string
  network_type: string
}

type EditFirewallRuleModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (ruleId: string, rule: FirewallRuleUpdate) => Promise<void>
  rule: FirewallRule | null
  vmName?: string
  vmInterfaces?: VMInterface[]
}

const DIRECTIONS: { value: FirewallDirection; label: string; description: string }[] = [
  { value: 'in', label: 'Entrada', description: 'Tráfego entrando na VM' },
  { value: 'out', label: 'Saída', description: 'Tráfego saindo da VM' },
]

const ACTIONS: { value: FirewallAction; label: string; description: string }[] = [
  { value: 'ACCEPT', label: 'Permitir', description: 'Permite o tráfego' },
  { value: 'DROP', label: 'Descartar', description: 'Descarta silenciosamente' },
  { value: 'REJECT', label: 'Rejeitar', description: 'Rejeita com resposta ICMP' },
]

const PROTOCOLS: { value: FirewallProtocol | ''; label: string }[] = [
  { value: '', label: 'Qualquer' },
  { value: 'tcp', label: 'TCP' },
  { value: 'udp', label: 'UDP' },
  { value: 'icmp', label: 'ICMP' },
  { value: 'sctp', label: 'SCTP' },
  { value: 'esp', label: 'ESP' },
  { value: 'ah', label: 'AH' },
  { value: 'gre', label: 'GRE' },
]

const EditFirewallRuleModal = ({
  isOpen,
  onClose,
  onSubmit,
  rule,
  vmName,
  vmInterfaces = [],
}: EditFirewallRuleModalProps) => {
  // Build dynamic interfaces list from VM data
  const INTERFACES = [
    { value: '', label: 'Todas', description: 'Aplica em todas as interfaces' },
    ...vmInterfaces.map((iface) => ({
      value: iface.name,
      label: iface.name,
      description: `${iface.network} (${iface.network_type === 'sdn_vnet' ? 'SDN VNet' : 'Bridge'})`,
    })),
  ]
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [comment, setComment] = useState('')
  const [direction, setDirection] = useState<FirewallDirection>('in')
  const [action, setAction] = useState<FirewallAction>('ACCEPT')
  const [protocol, setProtocol] = useState<FirewallProtocol | ''>('')
  const [dport, setDport] = useState('')
  const [sport, setSport] = useState('')
  const [source, setSource] = useState('')
  const [dest, setDest] = useState('')
  const [iface, setIface] = useState('')
  const [enabled, setEnabled] = useState(true)

  // Populate form when rule changes
  useEffect(() => {
    if (rule) {
      setName(rule.name || '')
      setComment(rule.comment || '')
      setDirection(rule.direction)
      setAction(rule.action)
      setProtocol(rule.protocol || '')
      setDport(rule.dport || '')
      setSport(rule.sport || '')
      setSource(rule.source || '')
      setDest(rule.dest || '')
      setIface(rule.iface || '')
      setEnabled(rule.enabled)
      setError(null)
    }
  }, [rule])

  const handleClose = () => {
    setError(null)
    onClose()
  }

  const validateForm = (): string | null => {
    // Validate port format if provided
    const portPattern = /^(\d{1,5}(:\d{1,5})?)(,\d{1,5}(:\d{1,5})?)*$/
    if (dport && !portPattern.test(dport)) {
      return 'Formato de porta de destino inválido. Use: 80, 80:443, ou 80,443'
    }
    if (sport && !portPattern.test(sport)) {
      return 'Formato de porta de origem inválido. Use: 80, 80:443, ou 80,443'
    }

    // Validate IP/CIDR format if provided
    const ipPattern = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/
    if (source && source.toLowerCase() !== 'any' && !ipPattern.test(source)) {
      return 'Formato de IP/CIDR de origem inválido. Use: 192.168.1.0/24 ou 192.168.1.1'
    }
    if (dest && dest.toLowerCase() !== 'any' && !ipPattern.test(dest)) {
      return 'Formato de IP/CIDR de destino inválido. Use: 192.168.1.0/24 ou 192.168.1.1'
    }

    // Protocol required for port rules
    if ((dport || sport) && !protocol) {
      return 'Selecione um protocolo ao especificar portas'
    }

    // TCP/UDP required for port rules
    if ((dport || sport) && protocol && !['tcp', 'udp', 'sctp'].includes(protocol)) {
      return 'Portas só podem ser usadas com protocolos TCP, UDP ou SCTP'
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!rule) return

    setError(null)

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    const update: FirewallRuleUpdate = {
      name: name || null,
      comment: comment || null,
      direction,
      action,
      protocol: protocol || null,
      dport: dport || null,
      sport: sport || null,
      source: source || null,
      dest: dest || null,
      iface: iface || null,
      enabled,
    }

    setIsSubmitting(true)
    try {
      await onSubmit(rule.id, update)
      handleClose()
    } catch (error) {
      // `getApiErrorText` e não `detail`: o handler da API reembrulha tudo em
      // `{error: {message}}`, então `detail` vinha `undefined` e o cliente lia
      // "Request failed with status code 409" no lugar do motivo — justamente nas
      // recusas novas por identidade, cuja mensagem existe para dizer o que fazer.
      // O `err.message` no meio cobre o `Error` próprio que o handler levanta quando a
      // resposta chega 2xx com `sync_error`.
      setError(getApiErrorText(error) || (error as Error)?.message || 'Erro ao atualizar regra')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!rule) return null

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-opacity-25 fixed inset-0 bg-black" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="rounded-lg bg-purple-100 p-2">
                      <ShieldIcon className="size-6 text-purple-600" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Editar Regra de Firewall
                      </Dialog.Title>
                      {vmName && <p className="text-sm text-gray-500">VM: {vmName}</p>}
                    </div>
                  </div>
                  <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                    <XIcon className="size-5" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-red-500" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                {rule.sync_error && (
                  <div className="mb-4 flex items-start space-x-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-amber-500" />
                    <div className="text-sm text-amber-700">
                      <strong>Erro de sincronização:</strong> {rule.sync_error}
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name & Comment */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Nome (opcional)
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ex: Web Server"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Comentário (opcional)
                      </label>
                      <input
                        type="text"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Ex: Allow HTTP traffic"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Direction & Action */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Direção
                      </label>
                      <Listbox value={direction} onChange={setDirection}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 focus:outline-none">
                            <span className="block truncate">
                              {DIRECTIONS.find((d) => d.value === direction)?.label}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="size-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black focus:outline-none">
                              {DIRECTIONS.map((dir) => (
                                <Listbox.Option
                                  key={dir.value}
                                  value={dir.value}
                                  className={({ active }) =>
                                    `relative cursor-pointer py-2 pr-4 pl-10 select-none ${
                                      active ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <div>
                                        <span
                                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                        >
                                          {dir.label}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {dir.description}
                                        </span>
                                      </div>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-600">
                                          <CheckIcon className="size-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Ação</label>
                      <Listbox value={action} onChange={setAction}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 focus:outline-none">
                            <span className="block truncate">
                              {ACTIONS.find((a) => a.value === action)?.label}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="size-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black focus:outline-none">
                              {ACTIONS.map((act) => (
                                <Listbox.Option
                                  key={act.value}
                                  value={act.value}
                                  className={({ active }) =>
                                    `relative cursor-pointer py-2 pr-4 pl-10 select-none ${
                                      active ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <div>
                                        <span
                                          className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                        >
                                          {act.label}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {act.description}
                                        </span>
                                      </div>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-600">
                                          <CheckIcon className="size-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                  </div>

                  {/* Protocol & Ports */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Protocolo
                      </label>
                      <Listbox value={protocol} onChange={setProtocol}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 focus:outline-none">
                            <span className="block truncate">
                              {PROTOCOLS.find((p) => p.value === protocol)?.label || 'Qualquer'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="size-5 text-gray-400" />
                            </span>
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black focus:outline-none">
                              {PROTOCOLS.map((proto) => (
                                <Listbox.Option
                                  key={proto.value}
                                  value={proto.value}
                                  className={({ active }) =>
                                    `relative cursor-pointer py-2 pr-4 pl-10 select-none ${
                                      active ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                      >
                                        {proto.label}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-600">
                                          <CheckIcon className="size-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </Listbox>
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Porta Destino
                      </label>
                      <input
                        type="text"
                        value={dport}
                        onChange={(e) => setDport(e.target.value)}
                        placeholder="80, 443, 1000:2000"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                        disabled={!protocol || !['tcp', 'udp', 'sctp'].includes(protocol)}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Porta Origem
                      </label>
                      <input
                        type="text"
                        value={sport}
                        onChange={(e) => setSport(e.target.value)}
                        placeholder="Opcional"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                        disabled={!protocol || !['tcp', 'udp', 'sctp'].includes(protocol)}
                      />
                    </div>
                  </div>

                  {/* Source & Dest */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        IP/CIDR Origem
                      </label>
                      <input
                        type="text"
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        placeholder="192.168.1.0/24 ou any"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        IP/CIDR Destino
                      </label>
                      <input
                        type="text"
                        value={dest}
                        onChange={(e) => setDest(e.target.value)}
                        placeholder="Opcional"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Interface */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Interface de Rede
                    </label>
                    <Listbox value={iface} onChange={setIface}>
                      <div className="relative">
                        <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-gray-300 bg-white py-2 pr-10 pl-3 text-left text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 focus:outline-none">
                          <span className="block truncate">
                            {INTERFACES.find((i) => i.value === iface)?.label || 'Todas'}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon className="size-5 text-gray-400" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="ring-opacity-5 absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black focus:outline-none">
                            {INTERFACES.map((int) => (
                              <Listbox.Option
                                key={int.value}
                                value={int.value}
                                className={({ active }) =>
                                  `relative cursor-pointer py-2 pr-4 pl-10 select-none ${
                                    active ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <div>
                                      <span
                                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                                      >
                                        {int.label}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {int.description}
                                      </span>
                                    </div>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-purple-600">
                                        <CheckIcon className="size-5" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  {/* Enabled */}
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="enabled-edit"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      className="size-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label htmlFor="enabled-edit" className="text-sm text-gray-700">
                      Regra ativa
                    </label>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2Icon className="mr-2 size-4 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        'Salvar Alterações'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default EditFirewallRuleModal
